import React, { useEffect, useState, useRef, createElement } from 'react';
import { ClockIcon, AlertCircleIcon, PlayIcon, RefreshCwIcon } from 'lucide-react';
export const Header = function Header() {
  // Countdown timer state - 59:59 (3599 seconds)
  const [timeRemaining, setTimeRemaining] = useState(3599);
  const [showVideo, setShowVideo] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const vidalyticsLoaded = useRef(false);
  const videoProgressChecked = useRef(false);
  // Constants
  const TIMER_STORAGE_KEY = 'countdownTimerStart';
  const VIDEO_TIMESTAMP_KEY = 'videoReached1820';
  const TARGET_TIMESTAMP = 1100; // 18:20 in seconds
  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  // Format the time remaining into MM:SS format
  const formatTimeRemaining = () => {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  // Determine if time is running low (less than 5 minutes)
  const isTimeLow = timeRemaining < 300;
  // Set up the countdown timer
  useEffect(() => {
    // Check if we have a stored start time
    const storedStartTime = localStorage.getItem(TIMER_STORAGE_KEY);
    let initialTimeRemaining = 3599;
    if (storedStartTime) {
      const startTime = parseInt(storedStartTime, 10);
      const currentTime = Math.floor(Date.now() / 1000);
      const elapsedTime = currentTime - startTime;
      // Calculate remaining time
      initialTimeRemaining = Math.max(3599 - elapsedTime, 0);
      // If more than an hour has passed, reset the timer
      if (initialTimeRemaining <= 0) {
        localStorage.setItem(TIMER_STORAGE_KEY, Math.floor(Date.now() / 1000).toString());
        initialTimeRemaining = 3599;
      }
    } else {
      // Store the current time as start time
      localStorage.setItem(TIMER_STORAGE_KEY, Math.floor(Date.now() / 1000).toString());
    }
    setTimeRemaining(initialTimeRemaining);
    // Set up the interval to decrease time
    const interval = setInterval(() => {
      setTimeRemaining(prevTime => {
        if (prevTime <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    // Clean up the interval
    return () => clearInterval(interval);
  }, []);
  // Auto-show video on load
  useEffect(() => {
    setShowVideo(true);
  }, []);
  // Manual skip function for testing
  const handleSkipToOffers = () => {
    localStorage.setItem(VIDEO_TIMESTAMP_KEY, 'true');
    window.dispatchEvent(new Event('storage'));
  };
  // Load Vidalytics script and set up progress tracking
  useEffect(() => {
    if (showVideo && !vidalyticsLoaded.current) {
      vidalyticsLoaded.current = true;
      // Create and append the Vidalytics loader script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.innerHTML = `
        (function (v, i, d, a, l, y, t, c, s) {
          y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
          if (!vsl){vsl=function(u,cb){
            if(t){cb();return;}s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
            if(s.readyState){s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};}else{s.onload=function(){vlf=1;cb();};}
            i.getElementsByTagName("head")[0].appendChild(s);
          };}
          vsl(l+'loader.min.js',function(){if(!vli){var vlc=v[c][vl];vli=new vlc();}vli.loadScript(l+'player.min.js',function(){var vec=v[d][ve];t=new vec();t.run(a);});});
        })(window, document, 'Vidalytics', 'vidalytics_embed_XtEbhBndWTU4xXSj', 'https://fast.vidalytics.com/embeds/UTlBIiHi/XtEbhBndWTU4xXSj/');
      `;
      document.body.appendChild(script);
      // Enhanced Vidalytics tracking with multiple methods
      let trackingAttempts = 0;
      const maxAttempts = 60; // Try for 60 seconds
      const checkInterval = setInterval(() => {
        trackingAttempts++;
        if (trackingAttempts > maxAttempts) {
          console.log('Vidalytics tracking timeout - player not found');
          clearInterval(checkInterval);
          return;
        }
        // Method 1: Try to access Vidalytics global object
        if (window.Vidalytics && !videoProgressChecked.current) {
          try {
            const players = window.Vidalytics.players;
            if (players && players.length > 0) {
              const player = players[0];
              console.log('Vidalytics player found:', player);
              // Set up time update listener
              const timeUpdateInterval = setInterval(() => {
                try {
                  let currentTime = null;
                  // Try multiple methods to get current time
                  if (typeof player.getCurrentTime === 'function') {
                    currentTime = player.getCurrentTime();
                  } else if (player.currentTime !== undefined) {
                    currentTime = player.currentTime;
                  } else if (player.player && player.player.currentTime) {
                    currentTime = player.player.currentTime;
                  }
                  if (currentTime !== null) {
                    console.log('Video time:', currentTime);
                    if (currentTime >= TARGET_TIMESTAMP && !localStorage.getItem(VIDEO_TIMESTAMP_KEY)) {
                      console.log('Target timestamp reached!');
                      localStorage.setItem(VIDEO_TIMESTAMP_KEY, 'true');
                      window.dispatchEvent(new Event('storage'));
                      videoProgressChecked.current = true;
                      clearInterval(timeUpdateInterval);
                    }
                  }
                } catch (error) {
                  console.error('Error checking video time:', error);
                }
              }, 1000);
              clearInterval(checkInterval);
            }
          } catch (error) {
            console.error('Error accessing Vidalytics player:', error);
          }
        }
        // Method 2: Try to find video element directly
        if (!videoProgressChecked.current) {
          const videoElement = document.querySelector('video');
          if (videoElement) {
            console.log('Video element found directly');
            videoElement.addEventListener('timeupdate', () => {
              const currentTime = videoElement.currentTime;
              if (currentTime >= TARGET_TIMESTAMP && !localStorage.getItem(VIDEO_TIMESTAMP_KEY)) {
                console.log('Target timestamp reached via video element!');
                localStorage.setItem(VIDEO_TIMESTAMP_KEY, 'true');
                window.dispatchEvent(new Event('storage'));
                videoProgressChecked.current = true;
              }
            });
            clearInterval(checkInterval);
          }
        }
      }, 1000);
    }
  }, [showVideo]);
  return <header className="w-full" data-id="element-146">
      {/* Breadcrumb Navigation */}
      <div className="bg-gray-100 py-2 px-4" data-id="element-147">
        <div className="max-w-6xl mx-auto" data-id="element-148">
          <nav className="text-sm text-gray-600" data-id="element-149">
            <ol className="flex" data-id="element-150">
              <li data-id="element-151">
                <a href="#" className="hover:underline" data-id="element-152">
                  Home
                </a>
              </li>
              <li data-id="element-153">
                <span className="mx-2" data-id="element-154">/</span>
              </li>
              <li data-id="element-155">
                <a href="#" className="hover:underline" data-id="element-156">
                  Haircare
                </a>
              </li>
              <li data-id="element-157">
                <span className="mx-2" data-id="element-158">/</span>
              </li>
              <li data-id="element-159">
                <a href="#" className="hover:underline" data-id="element-160">
                  Hair growth
                </a>
              </li>
            </ol>
          </nav>
        </div>
      </div>
      <div className="bg-blue-900 text-white text-center py-2 px-4 text-sm md:text-base font-bold" data-id="element-161">
        STANFORD UNIVERSITY PROFESSOR & HAIR LOSS SPECIALIST
      </div>
      <div className="bg-white text-center py-6 px-4" data-id="element-162">
        <h1 className="text-2xl md:text-3xl font-bold mb-4 text-black" data-id="element-163">
          Press Play To Begin Presentation Now
        </h1>
        <p className="text-lg md:text-xl mb-6 text-red-600 font-medium" data-id="element-164">
          IMPORTANT: Your video may take a few seconds to load.
        </p>
        {/* Vidalytics Video Container */}
        <div className="max-w-2xl mx-auto mb-2 rounded-lg overflow-hidden shadow-lg" data-id="element-165">
          {!showVideo ? <div className="relative" style={{
          paddingTop: '125%'
        }} data-id="element-166">
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-167">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jk7yyNMjhbeXbuMrMoCjHn/Doctor_sitting_at_the_desk.jpg" alt="Dr. John Nash - Hair Loss Specialist" className="w-full h-full object-cover absolute inset-0" loading="eager" fetchpriority="high" data-id="element-168" />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center" data-id="element-169">
                  <button onClick={() => setShowVideo(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full flex items-center" data-id="element-170">
                    <PlayIcon size={24} className="mr-2" data-id="element-171" />
                    Play Video
                  </button>
                </div>
              </div>
            </div> : <div className="relative" data-id="element-172">
              {/* Vidalytics Embed Container */}
              <div id="vidalytics_embed_XtEbhBndWTU4xXSj" style={{
            width: '100%',
            position: 'relative',
            paddingTop: '125%'
          }} data-id="element-173"></div>
            </div>}
        </div>
      </div>
    </header>;
};
export default {
  Header
};